import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Search, Image as ImageIcon, FolderOpen, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MediaGalleryPicker = ({ isOpen, onClose, onSelect, multiple = false }) => {
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [selectedFileType, setSelectedFileType] = useState('image');
  const [selectedItems, setSelectedItems] = useState([]);

  // Get auth token
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    if (isOpen) {
      fetchMedia();
      fetchFolders();
    }
  }, [isOpen, selectedFolder, selectedFileType]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedFileType !== 'all') params.append('file_type', selectedFileType);
      if (selectedFolder !== 'all') params.append('folder', selectedFolder);
      params.append('limit', '100'); // Fetch more items
      
      const response = await fetch(`${API_URL}/api/media/?${params.toString()}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setMedia(data);
    } catch (error) {
      console.error('Error fetching media:', error);
      toast.error('Failed to load media gallery');
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/media/folders/list`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setFolders(data);
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  const toggleSelection = (item) => {
    if (multiple) {
      const isSelected = selectedItems.some(i => i.id === item.id);
      if (isSelected) {
        setSelectedItems(selectedItems.filter(i => i.id !== item.id));
      } else {
        setSelectedItems([...selectedItems, item]);
      }
    } else {
      setSelectedItems([item]);
    }
  };

  const handleConfirm = () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one image');
      return;
    }
    
    // Check if file_url is already a full URL (CDN) or needs API_URL prefix
    const urls = selectedItems.map(item => {
      const fileUrl = item.file_url;
      return fileUrl.startsWith('http://') || fileUrl.startsWith('https://')
        ? fileUrl
        : `${API_URL}${fileUrl}`;
    });
    onSelect(multiple ? urls : urls[0]);
    setSelectedItems([]);
    onClose();
  };

  const handleCancel = () => {
    setSelectedItems([]);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filteredMedia = media.filter(item =>
    item.original_filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Select from Media Gallery</DialogTitle>
        </DialogHeader>
        
        {/* Filters */}
        <div className="flex gap-4 py-4 border-b">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={selectedFileType} onValueChange={setSelectedFileType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="image">Images</SelectItem>
              <SelectItem value="video">Videos</SelectItem>
              <SelectItem value="document">Documents</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedFolder} onValueChange={setSelectedFolder}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All folders" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Folders</SelectItem>
              {folders.map(folder => (
                <SelectItem key={folder.name} value={folder.name}>
                  {folder.name} ({folder.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Media Grid */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-12">Loading media...</div>
          ) : filteredMedia.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ImageIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>No media files found</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 p-4">
              {filteredMedia.map((item) => {
                const isSelected = selectedItems.some(i => i.id === item.id);
                return (
                  <Card 
                    key={item.id} 
                    className={`overflow-hidden cursor-pointer transition-all ${
                      isSelected ? 'ring-2 ring-blue-500 shadow-lg' : 'hover:shadow-lg'
                    }`}
                    onClick={() => toggleSelection(item)}
                  >
                    <div className="aspect-square bg-gray-100 flex items-center justify-center relative">
                      {item.file_type === 'image' ? (
                        <img 
                          src={item.file_url.startsWith('http') ? item.file_url : `${API_URL}${item.file_url}`} 
                          alt={item.alt_text || item.original_filename} 
                          className="w-full h-full object-cover" 
                        />
                      ) : (
                        <div className="text-gray-400">
                          <ImageIcon className="h-8 w-8" />
                        </div>
                      )}
                      {isSelected && (
                        <div className="absolute inset-0 bg-blue-500 bg-opacity-30 flex items-center justify-center">
                          <div className="bg-blue-500 rounded-full p-2">
                            <Check className="h-6 w-6 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                    <CardContent className="p-2">
                      <p className="text-xs font-medium truncate">{item.original_filename}</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-gray-500">{formatFileSize(item.file_size)}</p>
                        {item.folder && (
                          <Badge variant="secondary" className="text-xs">
                            <FolderOpen className="h-2 w-2 mr-1" />
                            {item.folder}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer with selection count and actions */}
        <DialogFooter className="border-t pt-4">
          <div className="flex items-center justify-between w-full">
            <p className="text-sm text-gray-600">
              {selectedItems.length} {selectedItems.length === 1 ? 'item' : 'items'} selected
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} disabled={selectedItems.length === 0}>
                Select {selectedItems.length > 0 && `(${selectedItems.length})`}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MediaGalleryPicker;
